import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import { AlertTitle } from '@mui/material';
import { createTheme as createV5Theme, ThemeProvider as V5ThemeProvider } from '@mui/material/styles';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BarChartIcon from '@material-ui/icons/BarChart';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import BusinessIcon from '@material-ui/icons/Business';
import HistoryIcon from '@material-ui/icons/History';
import DescriptionIcon from '@material-ui/icons/Description';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import CssBaseline from '@material-ui/core/CssBaseline';
import { createMuiTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';

import { useColorMode } from '../colorMode';
import { colors, darkVariables, lightVariables, palettes } from '../theme';
import AccountMenu from './AccountMenu';
import BrandHeader from './BrandHeader';

const SIDEBAR = 232;

// Навигация повторяет макет платформы. Экраны, которых пока нет,
// показаны неактивными — чтобы состав меню совпадал с презентацией,
// но никто не нажимал на пустоту.
const NAV = [
  { key: 'broadcast', label: 'Рассылки', icon: MailOutlineIcon, to: '/whatsapp' },
  { key: 'history', label: 'История', icon: HistoryIcon, to: '/whatsapphistory' },
  { key: 'lists', label: 'Списки', icon: FormatListBulletedIcon, to: '/whatsapplists' },
  { key: 'messages', label: 'Сообщения', icon: QuestionAnswerIcon, to: '/whatsappmessages' },
  { key: 'templates', label: 'Шаблоны', icon: DescriptionIcon, to: '/whatsapptemplates' },
  { key: 'stats', label: 'Статистика', icon: BarChartIcon, to: '/whatsappstats' },
  { key: 'account', label: 'Аккаунт', icon: BusinessIcon, to: '/whatsappaccount' },
];

// Наборы цветов включаются на <html>, а не на обёртке: окна рисуются вне страницы и иначе
// остались бы светлыми. Пока раздел закрыт, стили не подключены, и остальная система не меняется.
const useThemeStyles = makeStyles({
  '@global': {
    ':root': { ...lightVariables },
    ':root[data-wa-theme="dark"]': { ...darkVariables, colorScheme: 'dark' },
  },
});

// Тема компонентов Material-UI под выбранный набор цветов.
const muiThemeFor = (mode) => {
  const palette = palettes[mode];
  return {
    palette: {
      type: mode,
      primary: { main: palette.accent },
      background: { default: palette.bg, paper: palette.panel },
    },
  };
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: colors.bg,
  },
  // Сайдбар не участвует в прокрутке страницы: он прибит к окну, поэтому
  // ссылка возврата всегда видна снизу слева.
  sidebar: {
    width: SIDEBAR,
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 10,
    backgroundColor: colors.panel,
    borderRight: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    paddingTop: 10,
    flexGrow: 1,
    // Если пунктов станет больше, чем влезает в экран, скроллится само меню,
    // а не страница — ссылка возврата остаётся на месте.
    overflowY: 'auto',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 22px',
    color: colors.textDim,
    textDecoration: 'none',
    borderLeft: '3px solid transparent',
    fontSize: '14px',
    '&:hover': {
      color: colors.text,
      backgroundColor: colors.panelAlt,
    },
  },
  itemActive: {
    color: colors.accent,
    backgroundColor: colors.accentSoft,
    borderLeft: `3px solid ${colors.accent}`,
    '&:hover': { color: colors.accent, backgroundColor: colors.accentSoft },
  },
  itemDisabled: {
    color: colors.textFaint,
    cursor: 'default',
    '&:hover': { color: colors.textFaint, backgroundColor: 'transparent' },
  },
  icon: {
    fontSize: '19px',
    marginRight: 13,
  },
  themeRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 22px 6px 22px',
    color: colors.textDim,
    fontSize: '13px',
    borderTop: `1px solid ${colors.border}`,
  },
  themeLabel: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 22px',
    borderTop: `1px solid ${colors.border}`,
    color: colors.textDim,
    textDecoration: 'none',
    fontSize: '13px',
    '&:hover': { color: colors.text },
  },
  content: {
    flexGrow: 1,
    marginLeft: SIDEBAR,
    padding: '30px 34px',
    minWidth: 0,
  },
  alertWrap: {
    marginBottom: 20,
  },
});

const ALERT_MS = 6000;
const ALERT_SLOW_MS = 12000;

// Оболочка раздела рассылок: своё меню, как в макете платформы.
function PlatformShell(props) {
  useThemeStyles();
  const classes = useStyles();
  const { active, alert, hideAlert, children } = props;
  const [mode, toggleMode] = useColorMode();
  const muiTheme = useMemo(() => createMuiTheme(muiThemeFor(mode)), [mode]);
  const v5Theme = useMemo(() => createV5Theme({ palette: { mode } }), [mode]);

  // Уведомление закрывается само: ошибки и предупреждения висят дольше, чтобы их успели прочитать.
  useEffect(() => {
    if (!alert || !alert.show) return undefined;
    const slow = alert.severity === 'error' || alert.severity === 'warning';
    const timer = setTimeout(
      () => hideAlert({ ...alert, show: false }),
      slow ? ALERT_SLOW_MS : ALERT_MS
    );
    return () => clearTimeout(timer);
  }, [alert, hideAlert]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className={classes.root}>
        <div className={classes.sidebar}>
          <BrandHeader />
          <div className={classes.nav}>
            {NAV.map((item) => {
              const Icon = item.icon;
              if (!item.to) {
                return (
                  <Tooltip key={item.key} title="Экран ещё не реализован" placement="right">
                    <div className={`${classes.item} ${classes.itemDisabled}`}>
                      <Icon className={classes.icon} />
                      {item.label}
                    </div>
                  </Tooltip>
                );
              }
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`${classes.item} ${
                    active === item.key ? classes.itemActive : ''
                  }`}
                >
                  <Icon className={classes.icon} />
                  {item.label}
                </Link>
              );
            })}
          </div>
          <div className={classes.themeRow}>
            <span className={classes.themeLabel}>
              <Brightness4Icon className={classes.icon} />
              Тёмная тема
            </span>
            <Switch
              size="small"
              color="primary"
              checked={mode === 'dark'}
              onChange={toggleMode}
              inputProps={{ 'aria-label': 'Тёмная тема' }}
            />
          </div>
          <AccountMenu />
          <Link to="/companylist" className={classes.back}>
            <ArrowBackIcon style={{ fontSize: '17px', marginRight: 10 }} />
            В основную систему
          </Link>
        </div>

        <div className={classes.content}>
          {alert && alert.show ? (
            <div className={classes.alertWrap}>
              <V5ThemeProvider theme={v5Theme}>
                <Alert
                  onClose={hideAlert}
                  variant={alert.variant}
                  severity={alert.severity}
                >
                  <AlertTitle>{alert.alertTitle}</AlertTitle>
                  {alert.alertText}
                </Alert>
              </V5ThemeProvider>
            </div>
          ) : null}
          {children}
        </div>
      </div>
    </ThemeProvider>
  );
}

export { colors };
export default PlatformShell;

// Заголовок страницы в стиле макета: рубрика акцентом и крупный титул. actions — то, что стоит
// справа на одной строке с титулом (например, «Обновить»); gap — отступ под заголовком (26 по умолчанию).
export function PageTitle(props) {
  return (
    <div style={{ marginBottom: props.gap !== undefined ? props.gap : 26 }}>
      <Typography
        style={{
          color: colors.accent,
          fontSize: '11px',
          letterSpacing: '3px',
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        {props.kicker}
      </Typography>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography style={{ color: colors.text, fontSize: '27px', fontWeight: 700, flexGrow: 1 }}>
          {props.title}
        </Typography>
        {props.actions || null}
      </div>
      {props.subtitle ? (
        <Typography
          style={{ color: colors.textDim, fontSize: '13.5px', marginTop: 5 }}
        >
          {props.subtitle}
        </Typography>
      ) : null}
    </div>
  );
}
